import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ConnectionError } from "@formant/core/common/ConnectionError";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { IValidation } from "@formant/core/model/IValidation";
import { animate } from "@formant/ui/utils/animate";

import { track } from "../../analytics";
import { analyticsStore, observeStore, organizationStore } from "../../stores";
import { Buttons } from "../common/Buttons";
import { Escape } from "../common/Escape";
import { Form } from "../common/Form";
import { Heading3 } from "../common/Heading3";
import { Popup } from "../common/Popup";
import { RadioGroup } from "../common/RadioGroup";
import { StandardButton } from "../common/StandardButton";
import { ValidationMessages } from "../common/ValidationMessages";
import * as styles from "./ExportTelemetryPopupAnalytics.scss";

export interface IExportTelemetryPopupProps {
    query: ISqlQuery;
}

@observer
export class ExportTelemetryPopupAnalytics extends Component<IExportTelemetryPopupProps> {
    @observable
    private validation: IValidation | undefined;

    @observable
    private loading: boolean = false;

    @observable
    private visible: boolean = true;

    @observable
    private exportType: "CSV" = "CSV";

    public render() {
        const {
            validation,
            loading,
            visible,
            onSubmit,
            onLoading,
            onChangeExportType,
            onClose,
        } = this;

        if (!organizationStore.dataExportEnabled()) {
            return false;
        }

        return (
            <Popup
                visible={visible}
                onClose={onClose}
                className={styles["export-telemetry-popup"]}
            >
                <Form onSubmit={onSubmit} onLoading={onLoading}>
                    <Escape onEsc={onClose} />
                    <Heading3 className={styles.heading}>
                        Export Telemetry
                    </Heading3>
                    <p>Export Formant telemetry data.</p>
                    <ValidationMessages
                        className={styles.validation}
                        validation={validation}
                    />
                    {organizationStore.isGoogleSheetsEnabled && (
                        <RadioGroup
                            value={this.exportType}
                            options={[{ value: "CSV" }]}
                            onChange={onChangeExportType}
                            data-testid="export-radiobutton"
                        />
                    )}
                    <Buttons className={styles.buttons}>
                        <StandardButton
                            id="close-button"
                            onClick={onClose}
                            type="button"
                            data-testid="cancel-button"
                        >
                            Cancel
                        </StandardButton>
                        <StandardButton
                            primary={true}
                            loading={loading}
                            data-testid="export-button"
                        >
                            Export
                        </StandardButton>
                    </Buttons>
                </Form>
            </Popup>
        );
    }

    private onSubmit = async () => {
        this.validation = undefined;
        track("Export analytics module");
        try {
            await analyticsStore.exportSheet(this.props.query);
            await this.onClose();
        } catch (error) {
            if (error instanceof ConnectionError) {
                this.validation = {
                    other: ["Network request failed. Please try again."],
                };
                return;
            }
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }
    };

    private onClose = async () => {
        this.visible = false;
        await animate();
        observeStore.exportAnalyticsPopupQuery = undefined;
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };

    private onChangeExportType = (_: string): void => {
        this.exportType = "CSV";
    };
}
